import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { ArrowRight, ArrowLeft, List, Map, TrendingUp, Columns, AlertTriangle, Loader2, SendToBack } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { ColumnSelectorModal, type ColumnDefinition } from './ui/ColumnSelectorModal';
import type { WeldData } from './WeldDataTable';
import type { WeldPass } from './PassViewTable';
import { useWeldPasses, useZoneView, useTiltView } from '../redux/WeldData/hooks/useWeldData';
import { useProjectsLoader } from '../redux/WeldData/hooks/useWeldData';
import { useProjectManager } from '../redux/WeldData/hooks/useProjectManager';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWeldPasses, fetchZoneView, fetchTiltView, clearAllWeldViews, restoreMergedPasses } from '../redux/WeldData/actions';
import { createUpdateUserTableLayout, getAllUserTableLayouts } from '../redux/UserTableLayout/actions';
import ZoneViewTable from './ZoneViewTable';
import TiltViewTable from './TiltViewTable';
import { PassViewTable } from './PassViewTable';
import WeldPassDetailModal from './WeldPassDetailModal';
import ZoneViewDetailModal from './ZoneViewDetailModal';
import TiltViewDetailModal from './TiltViewDetailModal';

interface WeldPassListProps {
  isOpen: boolean;
  onClose: () => void;
  weld: WeldData;
  assemblyNumber: string;
  statusCalculationMethod?: string;
  // Separate status calculation methods for each view
  passViewStatusMethod?: string;
  zoneViewStatusMethod?: string;
  tiltViewStatusMethod?: string;
  statusCalculationStartedAt?: number | null;
  canEdit?: boolean;
  selectedPipe?: any;
}

// Memoized ZoneViewTable component
const MemoizedZoneViewTable = React.memo(({ weld, onRowClick, filters, onFilterChange, tableLimitRecords, isM500, statusCalculationMethod, visibleColumns, isCustomerRepresentative, isBuckleArrestorProject, statusCalculationStartedAt }: { 
  weld: WeldData; 
  onRowClick?: (row: any) => void; 
  filters: any;
  onFilterChange: (filterType: string, value: any) => void;
  tableLimitRecords?: any[];
  isM500?: boolean;
  statusCalculationMethod?: string;
  visibleColumns?: string[];
  isCustomerRepresentative?: boolean;
  isBuckleArrestorProject?: boolean;
  statusCalculationStartedAt?: number | null;
}) => (
  <ZoneViewTable 
    weld={weld} 
    onRowClick={onRowClick} 
    filters={filters} 
    onFilterChange={onFilterChange}
    tableLimitRecords={tableLimitRecords}
    isM500={isM500}
    statusCalculationMethod={statusCalculationMethod}
    visibleColumns={visibleColumns}
    isCustomerRepresentative={isCustomerRepresentative}
    isBuckleArrestorProject={isBuckleArrestorProject}
    statusCalculationStartedAt={statusCalculationStartedAt}
  />
));

MemoizedZoneViewTable.displayName = 'MemoizedZoneViewTable';

// Memoized TiltViewTable component
const MemoizedTiltViewTable = React.memo(({ weld, onRowClick, filters, onFilterChange, statusCalculationMethod, visibleColumns, statusCalculationStartedAt }: { 
  weld: WeldData; 
  onRowClick?: (row: any) => void; 
  filters: any;
  onFilterChange: (filterType: string, value: any) => void;
  statusCalculationMethod?: string;
  visibleColumns?: string[];
  statusCalculationStartedAt?: number | null;
}) => (
  <TiltViewTable weld={weld} onRowClick={onRowClick} filters={filters} onFilterChange={onFilterChange} statusCalculationMethod={statusCalculationMethod} visibleColumns={visibleColumns} statusCalculationStartedAt={statusCalculationStartedAt} />
));

MemoizedTiltViewTable.displayName = 'MemoizedTiltViewTable';

// Column definitions for each view
const PASS_VIEW_COLUMNS: ColumnDefinition[] = [
  // Sticky columns
  { key: 'sl-no', label: 'Sl.no', category: 'Basic Info' },
  { key: 'status', label: 'Status', category: 'Basic Info' },
  { key: 'job-number', label: 'Job Number', category: 'Basic Info' },
  { key: 'station', label: 'Station', category: 'Basic Info' },
  { key: 'actions', label: 'Actions', category: 'Basic Info' },
  // Scrollable columns
  { key: 'welder-id', label: 'Welder ID', category: 'Basic Info' },
  { key: 'bug-type', label: 'Bug Type', category: 'Basic Info' },
  { key: 'torch', label: 'Torch', category: 'Basic Info' },
  { key: 'pass-name', label: 'Pass Name', category: 'Basic Info' },
  { key: 'weld-start-time', label: 'Weld Start Time', category: 'Basic Info' },
  { key: 'weld-time', label: 'Weld Time', category: 'Basic Info' },
  { key: 'auto-increment', label: 'Auto Increment', category: 'Basic Info' },
  // Parameter columns
  { key: 'current', label: 'Current (A)', category: 'Parameters', subColumns: [
    { key: 'current-min', label: 'Min' },
    { key: 'current-avg', label: 'Avg' },
    { key: 'current-max', label: 'Max' }
  ]},
  { key: 'voltage', label: 'Voltage (V)', category: 'Parameters', subColumns: [
    { key: 'voltage-min', label: 'Min' },
    { key: 'voltage-avg', label: 'Avg' },
    { key: 'voltage-max', label: 'Max' }
  ]},
  { key: 'travel-speed', label: 'Travel Speed', category: 'Parameters', subColumns: [
    { key: 'travel-speed-min', label: 'Min' },
    { key: 'travel-speed-avg', label: 'Avg' },
    { key: 'travel-speed-max', label: 'Max' }
  ]},
  { key: 'true-energy', label: 'True Energy', category: 'Parameters', subColumns: [
    { key: 'true-energy-min', label: 'Min' },
    { key: 'true-energy-avg', label: 'Avg' },
    { key: 'true-energy-max', label: 'Max' }
  ]},
  { key: 'heat', label: 'Heat', category: 'Parameters', subColumns: [
    { key: 'heat-min', label: 'Min' },
    { key: 'heat-avg', label: 'Avg' },
    { key: 'heat-max', label: 'Max' }
  ]},
  { key: 'wire-speed', label: 'Wire Speed', category: 'Parameters', subColumns: [
    { key: 'wire-speed-min', label: 'Min' },
    { key: 'wire-speed-avg', label: 'Avg' },
    { key: 'wire-speed-max', label: 'Max' }
  ]},
  { key: 'oscillation-width', label: 'Oscillation Width', category: 'Parameters', subColumns: [
    { key: 'oscillation-width-min', label: 'Min' },
    { key: 'oscillation-width-avg', label: 'Avg' },
    { key: 'oscillation-width-max', label: 'Max' }
  ]},
  { key: 'frequency', label: 'Frequency', category: 'Parameters', subColumns: [
    { key: 'frequency-min', label: 'Min' },
    { key: 'frequency-avg', label: 'Avg' },
    { key: 'frequency-max', label: 'Max' }
  ]},
  { key: 'target', label: 'Target', category: 'Parameters', subColumns: [
    { key: 'target-min', label: 'Min' },
    { key: 'target-avg', label: 'Avg' },
    { key: 'target-max', label: 'Max' }
  ]},
  { key: 'horizontal-bias', label: 'Horizontal Bias', category: 'Parameters', subColumns: [
    { key: 'horizontal-bias-min', label: 'Min' },
    { key: 'horizontal-bias-avg', label: 'Avg' },
    { key: 'horizontal-bias-max', label: 'Max' }
  ]},
  { key: 'total-wire-consumed', label: 'Total Wire Consumed', category: 'Parameters', subColumns: [
    { key: 'total-wire-consumed-min', label: 'Min' },
    { key: 'total-wire-consumed-avg', label: 'Avg' },
    { key: 'total-wire-consumed-max', label: 'Max' }
  ]},
  { key: 'arc-impedance', label: 'Arc Impedance (mOhm)', category: 'Parameters', subColumns: [
    { key: 'arc-impedance-min', label: 'Min' },
    { key: 'arc-impedance-avg', label: 'Avg' },
    { key: 'arc-impedance-max', label: 'Max' }
  ]},
];

const ZONE_VIEW_COLUMNS: ColumnDefinition[] = [
  // Sticky columns
  { key: 'sl-no', label: 'Sl.no', category: 'Basic Info' },
  { key: 'status', label: 'Status', category: 'Basic Info' },
  { key: 'job-number', label: 'Job Number', category: 'Basic Info' },
  { key: 'station', label: 'Station', category: 'Basic Info' },
  { key: 'actions', label: 'Actions', category: 'Basic Info' },
  // Scrollable columns
  { key: 'welder-id', label: 'Welder ID', category: 'Basic Info' },
  { key: 'bug-type', label: 'Bug Type', category: 'Basic Info' },
  { key: 'torch', label: 'Torch', category: 'Basic Info' },
  { key: 'pass', label: 'Pass', category: 'Basic Info' },
  { key: 'zone', label: 'Zone', category: 'Basic Info' },
  { key: 'pipe', label: 'Pipe', category: 'Basic Info' },
  { key: 'weld-start-time', label: 'Weld Start Time', category: 'Basic Info' },
  { key: 'weld-time', label: 'Weld Time', category: 'Basic Info' },
  { key: 'auto-increment', label: 'Auto Increment', category: 'Basic Info' },
  // Parameter columns
  { key: 'current', label: 'Current', category: 'Parameters', subColumns: [
    { key: 'current-min', label: 'Min' },
    { key: 'current-avg', label: 'Avg' },
    { key: 'current-max', label: 'Max' }
  ]},
  { key: 'voltage', label: 'Voltage', category: 'Parameters', subColumns: [
    { key: 'voltage-min', label: 'Min' },
    { key: 'voltage-avg', label: 'Avg' },
    { key: 'voltage-max', label: 'Max' }
  ]},
  { key: 'travel-speed', label: 'Travel Speed', category: 'Parameters', subColumns: [
    { key: 'travel-speed-min', label: 'Min' },
    { key: 'travel-speed-avg', label: 'Avg' },
    { key: 'travel-speed-max', label: 'Max' }
  ]},
  { key: 'true-energy', label: 'True Energy', category: 'Parameters', subColumns: [
    { key: 'true-energy-min', label: 'Min' },
    { key: 'true-energy-avg', label: 'Avg' },
    { key: 'true-energy-max', label: 'Max' }
  ]},
  { key: 'heat', label: 'Heat', category: 'Parameters', subColumns: [
    { key: 'heat-min', label: 'Min' },
    { key: 'heat-avg', label: 'Avg' },
    { key: 'heat-max', label: 'Max' }
  ]},
  { key: 'wire-speed', label: 'Wire Speed', category: 'Parameters', subColumns: [
    { key: 'wire-speed-min', label: 'Min' },
    { key: 'wire-speed-avg', label: 'Avg' },
    { key: 'wire-speed-max', label: 'Max' }
  ]},
  { key: 'oscillation-width', label: 'Oscillation Width', category: 'Parameters', subColumns: [
    { key: 'oscillation-width-min', label: 'Min' },
    { key: 'oscillation-width-avg', label: 'Avg' },
    { key: 'oscillation-width-max', label: 'Max' }
  ]},
  { key: 'frequency', label: 'Frequency', category: 'Parameters', subColumns: [
    { key: 'frequency-min', label: 'Min' },
    { key: 'frequency-avg', label: 'Avg' },
    { key: 'frequency-max', label: 'Max' }
  ]},
  { key: 'target', label: 'Target', category: 'Parameters', subColumns: [
    { key: 'target-min', label: 'Min' },
    { key: 'target-avg', label: 'Avg' },
    { key: 'target-max', label: 'Max' }
  ]},
  { key: 'horizontal-bias', label: 'Horizontal Bias', category: 'Parameters', subColumns: [
    { key: 'horizontal-bias-min', label: 'Min' },
    { key: 'horizontal-bias-avg', label: 'Avg' },
    { key: 'horizontal-bias-max', label: 'Max' }
  ]},
  { key: 'total-wire-consumed', label: 'Total Wire Consumed', category: 'Parameters', subColumns: [
    { key: 'total-wire-consumed-min', label: 'Min' },
    { key: 'total-wire-consumed-avg', label: 'Avg' },
    { key: 'total-wire-consumed-max', label: 'Max' }
  ]},
  { key: 'arc-impedance', label: 'Arc Impedance', category: 'Parameters', subColumns: [
    { key: 'arc-impedance-min', label: 'Min' },
    { key: 'arc-impedance-avg', label: 'Avg' },
    { key: 'arc-impedance-max', label: 'Max' }
  ]},
];

const TILT_VIEW_COLUMNS: ColumnDefinition[] = [
  // Sticky columns
  { key: 'sl-no', label: 'Sl.no', category: 'Basic Info' },
  { key: 'status', label: 'Status', category: 'Basic Info' },
  { key: 'job-number', label: 'Job Number', category: 'Basic Info' },
  { key: 'station', label: 'Station', category: 'Basic Info' },
  { key: 'actions', label: 'Actions', category: 'Basic Info' },
  // Scrollable columns
  { key: 'bug-type', label: 'Bug Type', category: 'Basic Info' },
  { key: 'torch', label: 'Torch', category: 'Basic Info' },
  { key: 'pass', label: 'Pass', category: 'Basic Info' },
  { key: 'tilt-range', label: 'Tilt Range', category: 'Basic Info' },
  { key: 'weld-start-time', label: 'Weld Start Time', category: 'Basic Info' },
  { key: 'weld-time', label: 'Weld Time', category: 'Basic Info' },
  { key: 'auto-increment', label: 'Auto Increment', category: 'Basic Info' },
  // Parameter columns
  { key: 'current', label: 'Current', category: 'Parameters', subColumns: [
    { key: 'current-min', label: 'Min' },
    { key: 'current-avg', label: 'Avg' },
    { key: 'current-max', label: 'Max' }
  ]},
  { key: 'voltage', label: 'Voltage', category: 'Parameters', subColumns: [
    { key: 'voltage-min', label: 'Min' },
    { key: 'voltage-avg', label: 'Avg' },
    { key: 'voltage-max', label: 'Max' }
  ]},
  { key: 'travel-speed', label: 'Travel Speed', category: 'Parameters', subColumns: [
    { key: 'travel-speed-min', label: 'Min' },
    { key: 'travel-speed-avg', label: 'Avg' },
    { key: 'travel-speed-max', label: 'Max' }
  ]},
  { key: 'true-energy', label: 'True Energy', category: 'Parameters', subColumns: [
    { key: 'true-energy-min', label: 'Min' },
    { key: 'true-energy-avg', label: 'Avg' },
    { key: 'true-energy-max', label: 'Max' }
  ]},
  { key: 'heat', label: 'Heat', category: 'Parameters', subColumns: [
    { key: 'heat-min', label: 'Min' },
    { key: 'heat-avg', label: 'Avg' },
    { key: 'heat-max', label: 'Max' }
  ]},
  { key: 'wire-speed', label: 'Wire Speed', category: 'Parameters', subColumns: [
    { key: 'wire-speed-min', label: 'Min' },
    { key: 'wire-speed-avg', label: 'Avg' },
    { key: 'wire-speed-max', label: 'Max' }
  ]},
  { key: 'oscillation-width', label: 'Oscillation Width', category: 'Parameters', subColumns: [
    { key: 'oscillation-width-min', label: 'Min' },
    { key: 'oscillation-width-avg', label: 'Avg' },
    { key: 'oscillation-width-max', label: 'Max' }
  ]},
  { key: 'frequency', label: 'Frequency', category: 'Parameters', subColumns: [
    { key: 'frequency-min', label: 'Min' },
    { key: 'frequency-avg', label: 'Avg' },
    { key: 'frequency-max', label: 'Max' }
  ]},
  { key: 'target', label: 'Target', category: 'Parameters', subColumns: [
    { key: 'target-min', label: 'Min' },
    { key: 'target-avg', label: 'Avg' },
    { key: 'target-max', label: 'Max' }
  ]},
  { key: 'horizontal-bias', label: 'Horizontal Bias', category: 'Parameters', subColumns: [
    { key: 'horizontal-bias-min', label: 'Min' },
    { key: 'horizontal-bias-avg', label: 'Avg' },
    { key: 'horizontal-bias-max', label: 'Max' }
  ]},
  { key: 'total-wire-consumed', label: 'Total Wire Consumed', category: 'Parameters', subColumns: [
    { key: 'total-wire-consumed-min', label: 'Min' },
    { key: 'total-wire-consumed-avg', label: 'Avg' },
    { key: 'total-wire-consumed-max', label: 'Max' }
  ]},
  { key: 'arc-impedance', label: 'Arc Impedance', category: 'Parameters', subColumns: [
    { key: 'arc-impedance-min', label: 'Min' },
    { key: 'arc-impedance-avg', label: 'Avg' },
    { key: 'arc-impedance-max', label: 'Max' }
  ]},
];

// Table names for API
const TABLE_NAMES = {
  PASS_VIEW: 'weldPassList_passView',
  ZONE_VIEW: 'weldPassList_zoneView',
  TILT_VIEW: 'weldPassList_tiltView',
};

// Helper function to get all column keys including sub-columns
const getAllColumnKeys = (columns: ColumnDefinition[]): string[] => {
  const keys: string[] = [];
  columns.forEach(col => {
    keys.push(col.key);
    if (col.subColumns) {
      col.subColumns.forEach(sub => keys.push(sub.key));
    }
  });
  return keys;  
};

// Default parameter keys to show (only these 6 parameters by default)
const DEFAULT_PARAMETER_KEYS = [
  'current',
  'voltage',
  'travel-speed',
  // 'true-energy',
  'heat',
  'wire-speed'
];

// Helper function to get default column keys (only avg for parameters)
const getDefaultColumnKeys = (columns: ColumnDefinition[]): string[] => {
  const keys: string[] = [];
  columns.forEach(col => {
    // For parameters, only include the 6 default ones
    if (col.category === 'Parameters') {
      if (DEFAULT_PARAMETER_KEYS.includes(col.key)) {
        keys.push(col.key);
        // Only include avg sub-columns
        if (col.subColumns) {
          col.subColumns.forEach(sub => {
            if (sub.key.endsWith('-avg')) {
              keys.push(sub.key);
            }
          });
        }
      }
    } else {
      // Include all non-parameter columns (Basic Info, etc.)
      keys.push(col.key);
      if (col.subColumns) {
        col.subColumns.forEach(sub => {
          if (sub.key.endsWith('-avg')) {
            keys.push(sub.key);
          }
        });
      }
    }
  });
  return keys;
};

export const WeldPassList: React.FC<WeldPassListProps> = ({ 
  isOpen, 
  onClose, 
  weld, 
  assemblyNumber,
  statusCalculationMethod = 'Instantaneous',
  passViewStatusMethod = 'Instantaneous',
  zoneViewStatusMethod = 'AvgByZone', 
  tiltViewStatusMethod = 'AvgByTilt',
  statusCalculationStartedAt = null,
  canEdit = true,
  selectedPipe= null
}) => {
  const [activeTab, setActiveTab] = useState('pass-list');
  const dispatch = useDispatch();
  const { weldPasses, passesLoading, passesError, clearPasses } = useWeldPasses();
  const { zoneViewData, zoneViewLoading, zoneViewError, clearZoneViewData } = useZoneView();
  const { tiltViewData, tiltViewLoading, tiltViewError, clearTiltViewData } = useTiltView();
  const { projects, projectsLoading, projectsError } = useProjectsLoader();
  const { projectId: resolvedProjectId, projectName: resolvedProjectName, isProjectReady, currentProject } = useProjectManager();
  const lastWeldIdRef = useRef<string | null>(null);
  const [selectedPass, setSelectedPass] = useState<WeldPass | null>(null);
  const [selectedZone, setSelectedZone] = useState<any>(null);
  const [selectedTilt, setSelectedTilt] = useState<any>(null);

  // Get user table layouts from Redux state
  const userTableLayouts = useSelector((state: any) => state.userTableLayout?.layouts || []);
  const layoutsLoading = useSelector((state: any) => state.userTableLayout?.loading || false);

  // Store layout IDs for each view
  const layoutIdsRef = useRef<{
    passView: number | null;
    zoneView: number | null;
    tiltView: number | null;
  }>({
    passView: null,
    zoneView: null,
    tiltView: null
  });

  // Store default columns from API for each view (for Reset button in ColumnSelectorModal)
  const [defaultColumnsFromAPI, setDefaultColumnsFromAPI] = useState<{
    passView: string[] | null;
    zoneView: string[] | null;
    tiltView: string[] | null;
  }>({
    passView: null,
    zoneView: null,
    tiltView: null
  });

  // Column visibility state - initialize with default columns (only avg for parameters), will be updated from API
  const [passViewColumns, setPassViewColumns] = useState<string[]>(() => 
    getDefaultColumnKeys(PASS_VIEW_COLUMNS)
  );
  const [zoneViewColumns, setZoneViewColumns] = useState<string[]>(() => 
    getDefaultColumnKeys(ZONE_VIEW_COLUMNS)
  );
  const [tiltViewColumns, setTiltViewColumns] = useState<string[]>(() => 
    getDefaultColumnKeys(TILT_VIEW_COLUMNS)
  );

  // Column selector modal visibility state
  const [isPassViewColumnModalOpen, setIsPassViewColumnModalOpen] = useState(false);
  const [isZoneViewColumnModalOpen, setIsZoneViewColumnModalOpen] = useState(false);
  const [isTiltViewColumnModalOpen, setIsTiltViewColumnModalOpen] = useState(false);
  const [mergePassesEnabled, setMergePassesEnabled] = useState(false);
  const [showUnmergeConfirmation, setShowUnmergeConfirmation] = useState(false);

  const restoreMergedPassesLoading = useSelector((state: any) => state.weldData?.restoreMergedPassesLoading || false);

  // Load layouts from API when component mounts/opens
  useEffect(() => {
    if (isOpen) {
      // Saga will get userId from Redux state
      dispatch(getAllUserTableLayouts({ 
        table_name: 'weldPassList'
      }));
    }
  }, [isOpen, dispatch]);

  // Update column state when layouts are loaded from API
  useEffect(() => {
    if (userTableLayouts.length > 0) {
      
      // Find layouts for each view (saga already filtered by user_id)
      const passViewLayout = userTableLayouts.find(
        (layout: any) => layout.table_name === TABLE_NAMES.PASS_VIEW
      );
      const zoneViewLayout = userTableLayouts.find(
        (layout: any) => layout.table_name === TABLE_NAMES.ZONE_VIEW
      );
      const tiltViewLayout = userTableLayouts.find(
        (layout: any) => layout.table_name === TABLE_NAMES.TILT_VIEW
      );

      // Update layout IDs, columns, and default columns from API
      if (passViewLayout) {
        layoutIdsRef.current.passView = passViewLayout.user_table_layout_id;
        if (passViewLayout.layout?.columns && Array.isArray(passViewLayout.layout.columns)) {
          setPassViewColumns(passViewLayout.layout.columns);
          // Store as default columns for Reset button
          setDefaultColumnsFromAPI(prev => ({
            ...prev,
            passView: passViewLayout.layout.columns
          }));
        }
      }
      if (zoneViewLayout) {
        layoutIdsRef.current.zoneView = zoneViewLayout.user_table_layout_id;
        if (zoneViewLayout.layout?.columns && Array.isArray(zoneViewLayout.layout.columns)) {
          setZoneViewColumns(zoneViewLayout.layout.columns);
          // Store as default columns for Reset button
          setDefaultColumnsFromAPI(prev => ({
            ...prev,
            zoneView: zoneViewLayout.layout.columns
          }));
        }
      }
      if (tiltViewLayout) {
        layoutIdsRef.current.tiltView = tiltViewLayout.user_table_layout_id;
        if (tiltViewLayout.layout?.columns && Array.isArray(tiltViewLayout.layout.columns)) {
          setTiltViewColumns(tiltViewLayout.layout.columns);
          // Store as default columns for Reset button
          setDefaultColumnsFromAPI(prev => ({
            ...prev,
            tiltView: tiltViewLayout.layout.columns
          }));
        }
      }
    }
  }, [userTableLayouts]);

  // Filter states
  const [stationNameFilter, setStationNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [bugTypeFilter, setBugTypeFilter] = useState('all');
  const [torchFilter, setTorchFilter] = useState('all');
  const [passFilter, setPassFilter] = useState('all');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [dateRange, setDateRange] = useState<{ dateFrom: Date | undefined; dateTo: Date | undefined }>({
    dateFrom: undefined,
    dateTo: undefined
  });

  // Create a stable reference for the weld object
  const stableWeld = useMemo(() => weld, [weld.id, weld.weldNumber, weld.weldIdentifier]);

  // Debug logging
  useEffect(() => {
  }, [projects, projectsLoading, projectsError]);

  // Current project is now managed by the centralized project manager

  // Load all data when component opens - ALWAYS refresh when opening
  // Use ref to track if APIs are already called to prevent duplicate calls
  const apiCallsInProgressRef = useRef(false);
  const loadingWeldKeyRef = useRef<string | null>(null);
  
  useEffect(() => {
    // Only proceed if modal is open, we have weld data, and project is ready
    if (isOpen && weld && isProjectReady && resolvedProjectId && resolvedProjectName && weld.weldNumber) {
      // Create unique key for this weld
      const currentWeldKey = `${resolvedProjectId}-${weld.weldNumber}`;
      
      // CRITICAL: Check if we're already loading this exact weld OR if APIs are in progress
      // This prevents duplicate calls even in React StrictMode
      if (loadingWeldKeyRef.current === currentWeldKey || apiCallsInProgressRef.current) {
        return;
      }
      
      // Check if this is the same weld we just loaded
      const lastWeldKey = lastWeldIdRef.current ? `${resolvedProjectId}-${lastWeldIdRef.current}` : null;
      if (currentWeldKey === lastWeldKey) {
        return;
      }
      
      // IMMEDIATELY mark this weld as being loaded (before any async operations)
      loadingWeldKeyRef.current = currentWeldKey;
      apiCallsInProgressRef.current = true;
      
      // Clear previous data
      dispatch(clearAllWeldViews());
    //  console.log('weld.weldNumber', weld.weldNumber);
      //console.log('weld.jobNumber', weld.wpsNumber);
      
      // Small delay to ensure Redux state is cleared before fetching new data
      const loadTimer = setTimeout(() => {
        // Dispatch API calls with resolved LiveData360 project data
        // Pass wpsNumber as jobNumber (can be string, null, or undefined)
        const jobNumber: string | null | undefined = weld.wpsNumber ?? undefined;
        dispatch(fetchWeldPasses(resolvedProjectId, resolvedProjectName as any, weld.weldNumber, jobNumber as any));
        dispatch(fetchZoneView(resolvedProjectId, resolvedProjectName as any, weld.weldNumber, jobNumber as any));
        dispatch(fetchTiltView(resolvedProjectId, resolvedProjectName as any, weld.weldNumber, jobNumber as any));
        
        // Update refs
        lastWeldIdRef.current = weld.weldNumber;
        
        // Mark APIs as complete after a short delay
        setTimeout(() => {
          apiCallsInProgressRef.current = false;
          loadingWeldKeyRef.current = null;
        }, 1000);
      }, 100);
      
      return () => {
        clearTimeout(loadTimer);
      };
    }
  }, [isOpen, weld?.weldNumber, isProjectReady, resolvedProjectId, resolvedProjectName, dispatch]);

  // Clear all data when component closes - COMPLETE CLEANUP
  useEffect(() => {
    if (!isOpen) {
      dispatch(clearAllWeldViews());
      lastWeldIdRef.current = null; // Reset the last weld ID ref
      apiCallsInProgressRef.current = false; // Reset API call flag
      loadingWeldKeyRef.current = null; // Reset loading key
      
      // Also reset filters
      setStationNameFilter('');
      setStatusFilter('all');
      setBugTypeFilter('all');
      setTorchFilter('all');
      setPassFilter('all');
      setZoneFilter('all');
      setDateRange({ dateFrom: undefined, dateTo: undefined });
    }
  }, [isOpen, dispatch]);

  // Row click handler: replace modal with detail modal
  const handleRowClick = useCallback((data: WeldPass | any) => {
    // Check if it's a zone row (has both pass and zone)
    if (data.pass && data.zone) {
      setSelectedZone(data);
    } 
    // Check if it's a tilt row (has tiltRange)
    else if (data.tiltRange) {
      setSelectedTilt(data);
    } 
    // Otherwise it's a pass row
    else {
      setSelectedPass(data);
    }
  }, []);

  const handleDetailModalClose = useCallback(() => {
    setSelectedPass(null);
    setSelectedZone(null);
    setSelectedTilt(null);
  }, []);

  // Filter change handler
  const handleFilterChange = useCallback((filterType: string, value: any) => {
    switch (filterType) {
      case 'stationName':
        setStationNameFilter(value);
        break;
      case 'status':
        setStatusFilter(value);
        break;
      case 'bugType':
        setBugTypeFilter(value);
        break;
      case 'torch':
        setTorchFilter(value);
        break;
      case 'pass':
        setPassFilter(value);
        break;
      case 'zone':
        setZoneFilter(value);
        break;
      case 'dateRange':
        setDateRange(value);
        break;
    }
  }, []);

  // Create filters object to pass to components
  const filters = useMemo(() => ({
    stationNameFilter,
    statusFilter,
    bugTypeFilter,
    torchFilter,
    passFilter,
    zoneFilter,
    dateRange
  }), [stationNameFilter, statusFilter, bugTypeFilter, torchFilter, passFilter, zoneFilter, dateRange]);

  // Column visibility handlers - save to API
  const handlePassViewColumnsSave = useCallback((selectedColumns: string[]) => {
    setPassViewColumns(selectedColumns);
    
    const payload: any = {
      table_name: TABLE_NAMES.PASS_VIEW,
      layout: {
        columns: selectedColumns
      }
    };
    
    // If we have an existing layout ID, include it for update
    if (layoutIdsRef.current.passView) {
      payload.user_table_layout_id = layoutIdsRef.current.passView;
    }
    
    // Saga will add userId from Redux state
    dispatch(createUpdateUserTableLayout(payload));
  }, [dispatch]);

  const handleZoneViewColumnsSave = useCallback((selectedColumns: string[]) => {
    setZoneViewColumns(selectedColumns);
    
    const payload: any = {
      table_name: TABLE_NAMES.ZONE_VIEW,
      layout: {
        columns: selectedColumns
      }
    };
    
    // If we have an existing layout ID, include it for update
    if (layoutIdsRef.current.zoneView) {
      payload.user_table_layout_id = layoutIdsRef.current.zoneView;
    }
    
    // Saga will add userId from Redux state
    dispatch(createUpdateUserTableLayout(payload));
  }, [dispatch]);

  const handleTiltViewColumnsSave = useCallback((selectedColumns: string[]) => {
    setTiltViewColumns(selectedColumns);
    
    const payload: any = {
      table_name: TABLE_NAMES.TILT_VIEW,
      layout: {
        columns: selectedColumns
      }
    };
    
    // If we have an existing layout ID, include it for update
    if (layoutIdsRef.current.tiltView) {
      payload.user_table_layout_id = layoutIdsRef.current.tiltView;
    }
    
    // Saga will add userId from Redux state
    dispatch(createUpdateUserTableLayout(payload));
  }, [dispatch]);

  // Update layout IDs when create/update succeeds
  const createUpdateSuccess = useSelector((state: any) => state.userTableLayout?.currentLayout);
  useEffect(() => {
    if (createUpdateSuccess) {
      const layout = createUpdateSuccess;
      if (layout.table_name === TABLE_NAMES.PASS_VIEW) {
        layoutIdsRef.current.passView = layout.user_table_layout_id;
      } else if (layout.table_name === TABLE_NAMES.ZONE_VIEW) {
        layoutIdsRef.current.zoneView = layout.user_table_layout_id;
      } else if (layout.table_name === TABLE_NAMES.TILT_VIEW) {
        layoutIdsRef.current.tiltView = layout.user_table_layout_id;
      }
    }
  }, [createUpdateSuccess]);

  // Check if current project is a Buckle Arrestor project
  const isBuckleArrestorProject = useMemo(() => {
    return selectedPipe?.projectSubType === 'buckleArrestor';
  }, [selectedPipe]);

  // Parse weld.wpsNumber: if multiple (comma/semicolon-separated), show job number column; if one, hide column
  const { hasMultipleJobNumbers, formattedJobNumbers } = useMemo(() => {
    const raw = weld?.wpsNumber;
    if (raw == null || String(raw).trim() === '') {
      return { hasMultipleJobNumbers: false, formattedJobNumbers: '-' };
    }
    const parts = (Array.isArray(raw) ? raw : String(raw).split(/[,;]/).map((s: string) => s.trim())).filter(Boolean);
    const formatted = parts.join(', ');
    return {
      hasMultipleJobNumbers: parts.length > 1,
      formattedJobNumbers: formatted || '-'
    };
  }, [weld?.wpsNumber]);

  // Show job number column in tables only when there are multiple job numbers
  const passViewColumnsEffective = useMemo(
    () => (hasMultipleJobNumbers ? passViewColumns : passViewColumns.filter((c) => c !== 'job-number')),
    [hasMultipleJobNumbers, passViewColumns]
  );
  const zoneViewColumnsEffective = useMemo(
    () => (hasMultipleJobNumbers ? zoneViewColumns : zoneViewColumns.filter((c) => c !== 'job-number')),
    [hasMultipleJobNumbers, zoneViewColumns]
  );
  const tiltViewColumnsEffective = useMemo(
    () => (hasMultipleJobNumbers ? tiltViewColumns : tiltViewColumns.filter((c) => c !== 'job-number')),
    [hasMultipleJobNumbers, tiltViewColumns]
  );

  // Check if user is a Customer Representative
  const isCustomerRepresentative = useMemo(() => {
    return currentProject?.project?.team_member_role === 'Customer Representative';
  }, [currentProject]);

  // Handle active tab change if pass-list or tilt-view is hidden for buckle arrestor projects
  useEffect(() => {
    if (isBuckleArrestorProject && (activeTab === 'pass-list' || activeTab === 'tilt-view')) {
      setActiveTab('zone-view');
    }
  }, [isBuckleArrestorProject, activeTab]);

  const TABS_CONFIG = useMemo(() => {
    const allTabs = {
      'pass-list': {
        label: 'Pass Views',
        icon: List,
        content: () => (
          <PassViewTable 
            weldPasses={weldPasses}
            passesLoading={passesLoading}
            passesError={passesError}
            onRowClick={handleRowClick}
            filters={filters}
            onFilterChange={handleFilterChange}
            statusCalculationMethod={passViewStatusMethod}
            visibleColumns={passViewColumnsEffective}
            projectId={resolvedProjectId ?? undefined}
            projectName={resolvedProjectName ?? undefined}
            weld={weld}
            canEdit={canEdit}
            mergePassesEnabled={mergePassesEnabled}
            setMergePassesEnabled={setMergePassesEnabled}
            showUnmergeConfirmation={showUnmergeConfirmation}
            setShowUnmergeConfirmation={setShowUnmergeConfirmation}
            statusCalculationStartedAt={statusCalculationStartedAt}
          />
        )
      },
      'zone-view': {
        label: 'Zone View',
        icon: Map,
        content: () => (
          <MemoizedZoneViewTable 
            weld={stableWeld} 
            onRowClick={handleRowClick} 
            filters={filters}
            onFilterChange={handleFilterChange}
            tableLimitRecords={undefined}
            isM500={false}
            statusCalculationMethod={zoneViewStatusMethod}
            visibleColumns={zoneViewColumnsEffective}
            isCustomerRepresentative={isCustomerRepresentative}
            isBuckleArrestorProject={isBuckleArrestorProject}
            statusCalculationStartedAt={statusCalculationStartedAt}
          />
        )
      },
      'tilt-view': {
        label: 'Tilt View',
        icon: TrendingUp,
        content: () => (
          <MemoizedTiltViewTable 
            weld={stableWeld} 
            onRowClick={handleRowClick} 
            filters={filters}
            onFilterChange={handleFilterChange}
            statusCalculationMethod={tiltViewStatusMethod}
            visibleColumns={tiltViewColumnsEffective}
            statusCalculationStartedAt={statusCalculationStartedAt}
          />
        )
      }
    } as const;

    // Filter out 'pass-list' and 'tilt-view' tabs for Buckle Arrestor projects (only show zone-view)
    if (isBuckleArrestorProject) {
      const { 'pass-list': _, 'tilt-view': __, ...filteredTabs } = allTabs;
      return filteredTabs;
    }

    return allTabs;
  }, [weldPasses, passesLoading, passesError, stableWeld, handleRowClick, filters, handleFilterChange, passViewStatusMethod, zoneViewStatusMethod, tiltViewStatusMethod, statusCalculationStartedAt, passViewColumnsEffective, zoneViewColumnsEffective, tiltViewColumnsEffective, canEdit, mergePassesEnabled, showUnmergeConfirmation, isBuckleArrestorProject, isCustomerRepresentative, resolvedProjectId, resolvedProjectName, weld]);

  // If a pass is selected, show the pass detail modal
  if (selectedPass) {
    return (
      <WeldPassDetailModal isOpen={isOpen} onClose={handleDetailModalClose} passData={selectedPass} clickedRowData={selectedPass} weldData={weld} statusCalculationMethod={passViewStatusMethod} canEdit={canEdit} />
    );
  }

  // If a zone is selected, show the zone detail modal
  if (selectedZone) {
    return (
      <ZoneViewDetailModal isOpen={isOpen} onClose={handleDetailModalClose} zoneData={selectedZone} weldData={weld} statusCalculationMethod={zoneViewStatusMethod} canEdit={canEdit} />
    );
  }

  // If a tilt is selected, show the tilt detail modal
  if (selectedTilt) {
    return (
      <TiltViewDetailModal isOpen={isOpen} onClose={handleDetailModalClose} tiltData={selectedTilt} weldData={weld} statusCalculationMethod={tiltViewStatusMethod} canEdit={canEdit} />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0 overflow-hidden">
        <div className="flex flex-col h-full">
          <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between w-full bg-white">
            <div className="flex items-center gap-4">
              <Button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                variant="outline"
              >
                <ArrowLeft className="h-5 w-5 text-gray-500" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">{weld.weldNumber || weld.id || '-'}</h1>
                <div className="text-sm text-gray-600 mt-1">
                  Job Number: {formattedJobNumbers}
                </div>
              </div>
            </div>
            {canEdit && activeTab === 'pass-list' && (
              <div className="flex items-center gap-2 ml-4">
                <Checkbox
                  checked={mergePassesEnabled}
                  onCheckedChange={(checked) => setMergePassesEnabled(checked === true)}
                  title="Merge Passes"
                  className="h-5 w-5"
                />
                <Label className="px-2">Merge passes</Label>
                {mergePassesEnabled && (
                  <Button
                    onClick={() => setShowUnmergeConfirmation(true)}
                    disabled={restoreMergedPassesLoading}
                    className="px-2 py-1"
                  >
                    <SendToBack className="h-4 w-4" /> Un Merge Passes
                  </Button>
                )}
              </div>
            )}
          </DialogHeader>
          <div className="flex-1 flex flex-col overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col">
              <div className="flex-shrink-0 sticky top-0 bg-white rounded-t-lg z-10 border-b border-gray-200">
                <div className="flex items-center justify-between px-1 md:px-4 py-2">
                  <TabsList className="h-auto flex-1 justify-start gap-2 md:gap-4 lg:gap-8 border-b bg-transparent py-2 flex-wrap">
                    {Object.entries(TABS_CONFIG).map(([key, config]) => (
                      <TabsTrigger
                        key={key}
                        value={key}
                        className="data-[state=active]:border-[#EB1C37] data-[state=active]:text-[#EB1C37] relative px-1 pb-3 pt-2 text-sm md:text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px] whitespace-nowrap"
                      >
                        <config.icon className="inline-block mr-1 h-4 w-4 align-text-bottom" />
                        {config.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  <div className="flex gap-2 px-2">
                    {activeTab === 'pass-list' && (
                      <Button
                        onClick={() => setIsPassViewColumnModalOpen(true)}
                        className="flex items-center gap-2"
                        title="Configure columns"
                      >
                        <Columns className="h-4 w-4" />
                        <span className="hidden sm:inline">Columns</span>
                      </Button>
                    )}
                    {activeTab === 'zone-view' && (
                      <Button
                        onClick={() => setIsZoneViewColumnModalOpen(true)}
                        className="flex items-center gap-2"
                        title="Configure columns"
                      >
                        <Columns className="h-4 w-4" />
                        <span className="hidden sm:inline">Columns</span>
                      </Button>
                    )}
                    {activeTab === 'tilt-view' && (
                      <Button
                        onClick={() => setIsTiltViewColumnModalOpen(true)}
                        className="flex items-center gap-2"
                        title="Configure columns"
                      >
                        <Columns className="h-4 w-4" />
                        <span className="hidden sm:inline">Columns</span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              {Object.entries(TABS_CONFIG).map(([key, config]) => (
                <TabsContent 
                  key={key} 
                  value={key} 
                  className="flex-1 m-0 p-0 data-[state=active]:flex data-[state=inactive]:hidden"
                  style={{ height: 'calc(100% - 72px)' }}
                >
                  {config.content()}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      </DialogContent>

      {/* Column Selector Modals */}
      <ColumnSelectorModal
        isOpen={isPassViewColumnModalOpen}
        onClose={() => setIsPassViewColumnModalOpen(false)}
        onSave={handlePassViewColumnsSave}
        columns={PASS_VIEW_COLUMNS}
        selectedColumns={passViewColumns}
        title="Select Columns - Pass View"
        defaultColumns={defaultColumnsFromAPI.passView || getDefaultColumnKeys(PASS_VIEW_COLUMNS)}
      />
      <ColumnSelectorModal
        isOpen={isZoneViewColumnModalOpen}
        onClose={() => setIsZoneViewColumnModalOpen(false)}
        onSave={handleZoneViewColumnsSave}
        columns={ZONE_VIEW_COLUMNS}
        selectedColumns={zoneViewColumns}
        title="Select Columns - Zone View"
        defaultColumns={defaultColumnsFromAPI.zoneView || getDefaultColumnKeys(ZONE_VIEW_COLUMNS)}
      />
      <ColumnSelectorModal
        isOpen={isTiltViewColumnModalOpen}
        onClose={() => setIsTiltViewColumnModalOpen(false)}
        onSave={handleTiltViewColumnsSave}
        columns={TILT_VIEW_COLUMNS}
        selectedColumns={tiltViewColumns}
        title="Select Columns - Tilt View"
        defaultColumns={defaultColumnsFromAPI.tiltView || getDefaultColumnKeys(TILT_VIEW_COLUMNS)}
      />


   

    </Dialog>
  );
}; 